import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ApartmentOutlined } from "@ant-design/icons";
import { Button, message, Modal, Space, Spin, Switch, Tooltip, Typography } from "antd";
import {
  RecommendationFlowConfig,
  RecommendationFlowStep,
  RecommendationRunApi,
} from "../../api/RecommendationRunApi";
import RecommendationJobIndicator from "./RecommendationJobIndicator";
import JobOriginIndicator from "./JobOriginIndicator";
import { DataScienceJobOrigin } from "../../api/DataScienceJobApi";

const { Text } = Typography;

type StepToggleKey =
  | "segment_engine_enabled"
  | "ds_segments_enabled"
  | "commercial_opportunities_enabled"
  | "next_purchase_enabled"
  | "rewards_enabled"
  | "promotions_enabled"
  | "activation_actions_enabled";

const STEP_TOGGLE_MAP: Record<string, StepToggleKey> = {
  SEGMENTS: "segment_engine_enabled",
  DS_SEGMENTS: "ds_segments_enabled",
  COMMERCIAL_OPPORTUNITIES: "commercial_opportunities_enabled",
  NEXT_PURCHASE: "next_purchase_enabled",
  REWARDS: "rewards_enabled",
  PROMOTIONS: "promotions_enabled",
  ACTIVATION_ACTIONS: "activation_actions_enabled",
};

const JOB_TYPE_ROUTE: Record<string, string> = {
  segment_engine: "segment_engine",
  segments: "segments",
  next_purchase: "next_purchase",
  rewards: "rewards",
  forecast_manual_promotions: "forecast_manual_promotions",
  promotions: "promotions",
};

const JOB_TYPE_ORIGIN: Record<string, DataScienceJobOrigin> = {
  segment_engine: "api_ecs",
  segments: "data_science",
  next_purchase: "data_science",
  rewards: "api",
  forecast_manual_promotions: "data_science",
  promotions: "data_science",
};

const SEGMENT_FLOW_STEPS = new Set(["SEGMENTS", "DS_SEGMENTS"]);

interface RecommendationFlowConfigCardProps {
  organizationId?: number;
  disabled?: boolean;
}

const RecommendationFlowConfigCard: React.FC<RecommendationFlowConfigCardProps> = ({
  organizationId,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState<RecommendationFlowConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingStep, setSavingStep] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    if (!organizationId) {
      setConfig(null);
      return;
    }

    try {
      setLoading(true);
      const data = await RecommendationRunApi.getFlowConfig(organizationId);
      setConfig(data);
    } catch (error) {
      message.error(t("DataScienceJobs-FlowConfigLoadError"));
    } finally {
      setLoading(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    if (open) {
      loadConfig();
    }
  }, [open, loadConfig]);

  const handleOpen = () => {
    if (!organizationId) {
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggle = async (step: RecommendationFlowStep, enabled: boolean) => {
    const toggleKey = STEP_TOGGLE_MAP[step.step];
    if (!toggleKey || !organizationId) {
      return;
    }

    try {
      setSavingStep(step.step);
      const updated = await RecommendationRunApi.updateFlowConfig({
        [toggleKey]: enabled,
        organization_id: organizationId,
      });
      setConfig(updated);
      message.success(t("DataScienceJobs-FlowConfigSaveSuccess"));
    } catch (error: any) {
      const apiError = error?.response?.data?.apierror;
      const backendMessage =
        apiError?.message_ES ??
        apiError?.message ??
        (typeof error?.response?.data === "string" ? error.response.data : null) ??
        error?.response?.data?.message;
      message.error(backendMessage || t("DataScienceJobs-FlowConfigSaveError"));
    } finally {
      setSavingStep(null);
    }
  };

  const handleConfigureJob = (jobType?: string) => {
    if (!jobType) {
      return;
    }
    setOpen(false);
    navigate(`/admin/datasciencejobs/${JOB_TYPE_ROUTE[jobType] || jobType}`);
  };

  const buttonDisabled = disabled || !organizationId;

  const openButton = (
    <Button icon={<ApartmentOutlined />} disabled={buttonDisabled} onClick={handleOpen}>
      {t("DataScienceJobs-FlowConfigOpenButton")}
    </Button>
  );

  return (
    <>
      {buttonDisabled && !organizationId ? (
        <Tooltip title={t("DataScienceJobs-FlowConfigSelectOrganization")}>
          <span>{openButton}</span>
        </Tooltip>
      ) : (
        openButton
      )}

      <Modal
        title={t("DataScienceJobs-FlowConfigTitle")}
        visible={open}
        onCancel={handleClose}
        footer={null}
        width={640}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          {t("DataScienceJobs-FlowConfigDescription")}
        </Text>

        <Spin spinning={loading}>
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {(config?.steps || []).map((step, index) => {
              const isLast = index === (config?.steps.length || 0) - 1;
              const toggleKey = STEP_TOGGLE_MAP[step.step];
              const isSaving = savingStep === step.step;

              return (
                <div key={step.step}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                      padding: "8px 0",
                    }}
                  >
                    <Space direction="vertical" size={0} style={{ flex: 1 }}>
                      <Space size={8}>
                        <Text strong={step.enabled}>
                          {step.label_key ? t(step.label_key) : step.label}
                        </Text>
                        {step.job_type && SEGMENT_FLOW_STEPS.has(step.step) && (
                          <JobOriginIndicator
                            origin={JOB_TYPE_ORIGIN[step.job_type] ?? "data_science"}
                            size={14}
                            tooltipTitle={t(`DataScienceJobs-JobTypes.${step.job_type}`)}
                          />
                        )}
                        {step.job_type && (
                          <RecommendationJobIndicator
                            configured={!!step.job_configured}
                            size={14}
                          />
                        )}
                      </Space>
                      {step.job_type && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {step.job_configured
                            ? t("DataScienceJobs-FlowConfigJobConfigured")
                            : t("DataScienceJobs-FlowConfigJobMissing")}
                          {" · "}
                          <Text
                            style={{ fontSize: 12, color: "#1890ff", cursor: "pointer" }}
                            onClick={() => handleConfigureJob(step.job_type)}
                          >
                            {t("DataScienceJobs-FlowConfigConfigureJob")}
                          </Text>
                        </Text>
                      )}
                      {!step.toggleable && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t("DataScienceJobs-FlowConfigAlwaysEnabled")}
                        </Text>
                      )}
                    </Space>

                    {step.toggleable && toggleKey ? (
                      <Switch
                        checked={step.enabled}
                        loading={isSaving}
                        disabled={disabled || loading}
                        onChange={(checked) => handleToggle(step, checked)}
                      />
                    ) : null}
                  </div>
                  {!isLast && (
                    <div
                      style={{
                        borderLeft: "2px solid #f0f0f0",
                        height: 12,
                        marginLeft: 8,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </Space>
        </Spin>
      </Modal>
    </>
  );
};

export default RecommendationFlowConfigCard;
